/**
 * 指标旁的悬浮定义（10-27 用户：涉及指标的页面都在指标旁放悬浮说明，结构＝回答的问题＋分子／分母）。
 * 定义只写在 `metric-defs.ts` 一处，页面按编号取；查不到编号只显示名称、不出图标。
 * 浮层挂到 body（表头外层是 overflow-x-auto，原地定位会被裁掉），按图标位置摆，贴边时往回收。
 */
import { CircleHelp } from "lucide-react";
import { type ReactNode, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/utils";

import { BETTER_ZH, METRIC_DEFS, type MetricDef, type MetricId } from "./metric-defs";

const W = 288;

export function MetricTip({
	id,
	children,
	className,
}: {
	id: MetricId;
	/** 页面上的指标名；不给用定义里的名称 */
	children?: ReactNode;
	className?: string;
}) {
	const def: MetricDef | undefined = METRIC_DEFS[id];
	const ref = useRef<HTMLSpanElement>(null);
	const [pos, setPos] = useState<{ left: number; top: number; up: boolean } | null>(null);
	const name = children ?? def?.name ?? id;
	if (!def) return <>{name}</>;

	const open = () => {
		const r = ref.current?.getBoundingClientRect();
		if (!r) return;
		const left = Math.max(8, Math.min(r.left + r.width / 2 - W / 2, window.innerWidth - W - 8));
		const up = r.bottom + 200 > window.innerHeight;
		setPos({ left, top: up ? r.top - 6 : r.bottom + 6, up });
	};
	const close = () => setPos(null);

	return (
		<span className={cn("inline-flex items-center gap-1", className)}>
			{name}
			<span
				ref={ref}
				// biome-ignore lint/a11y/noNoninteractiveTabindex: 键盘聚焦也能看到定义
				tabIndex={0}
				aria-label={`${def.name}的定义`}
				className="inline-flex cursor-help text-text-muted outline-none hover:text-text-secondary focus-visible:text-brand-accent"
				onMouseEnter={open}
				onMouseLeave={close}
				onFocus={open}
				onBlur={close}
				data-testid={`metric-tip-${id}`}
			>
				<CircleHelp className="h-3.5 w-3.5" aria-hidden />
			</span>
			{pos
				? createPortal(
						<div
							role="tooltip"
							className={cn(
								"pointer-events-none fixed z-[60] space-y-1.5 rounded-lg border border-surface-border bg-surface-card p-3 text-left text-xs normal-case tracking-normal text-text-secondary shadow-lg",
								pos.up && "-translate-y-full",
							)}
							style={{ left: pos.left, top: pos.top, width: W }}
						>
							<div className="font-sans text-sm font-semibold text-text-primary">{def.name}</div>
							<Row k="回答的问题" v={def.q} />
							<Row
								k={def.den ? "分子 ／ 分母" : "怎么算"}
								v={def.den ? `${def.num} ／ ${def.den}` : def.num}
							/>
							{def.note ? <Row k="口径" v={def.note} /> : null}
							{def.better ? <Row k="怎么看" v={BETTER_ZH[def.better]} /> : null}
						</div>,
						document.body,
					)
				: null}
		</span>
	);
}

function Row({ k, v }: { k: string; v: string }) {
	return (
		<div className="font-sans leading-relaxed">
			<span className="text-text-muted">{k}：</span>
			<span className="text-text-primary">{v}</span>
		</div>
	);
}
